import {useMutation} from "@apollo/client";
import omit from "lodash/omit";

import {ScheduleEntryInput, ScheduleEntryType} from "@/components/form/maintenance/types.ts";
import {
  GQL_CONTACT_GROUPS_LIST_QUERY,
  GQL_UPDATE_CONTACT_GROUP,
  UpdateContactGroupResult,
} from "@/lib/queries/contacts.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";
import {EditContactModal} from "@/pages/contacts/components/EditContactModal.tsx";
import {ContactFormValues} from "@/pages/contacts/components/form/EditContactForm.tsx";

interface EditContactModalActionProps {
  formId: string;
  show: boolean;
  setShow: (show: boolean) => void;
  contact?: ContactGroup;
}

export const EditContactModalAction = ({formId, contact, show, setShow}: EditContactModalActionProps) => {
  const [updateContact, {data, reset}] = useMutation<UpdateContactGroupResult>(GQL_UPDATE_CONTACT_GROUP, {
    refetchQueries: [GQL_CONTACT_GROUPS_LIST_QUERY],
    context: {noToast: true},
  });

  const cleanInput = (data: ContactFormValues) => {
    return {
      ...data,
      schedule_entries: data.schedule_entries.map((e: ScheduleEntryInput) => {
        if (e.type !== ScheduleEntryType.ONCE) {
          // skip once dates since API does not support blank values
          return omit(e, ["once_start_date", "once_end_date", "__typename"]);
        }
        return omit(e, ["__typename"]);
      }),
    };
  };
  const handleSubmit = async (data: ContactFormValues) => {
    const resp = await updateContact({
      variables: {
        data: cleanInput(data),
      },
    });
    if (resp.data?.response?.status === ResponseStatus.ERROR) {
      return;
    }
    reset();
    setShow(false);
  };

  const serverErrors = data?.response?.status === ResponseStatus.ERROR ? data?.response?.message : undefined;
  return (
    <EditContactModal
      defaultValues={contact}
      title={contact ? "Edit Contact" : "Create Contact"}
      formId={formId}
      show={show}
      onCancel={() => {
        reset();
        setShow(false);
      }}
      onSubmit={handleSubmit}
      serverErrors={serverErrors}
    />
  );
};
